import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { callPymbleApi } from "./usePymbleApi";

/** One row of `GET room/`: the fields this app reads. */
export interface RoomOption {
	key: string;
	name: string;
	capacity?: number | null;
	building?: string | null;
}

interface RoomListResponse {
	items?: RoomOption[];
}

const EMPTY: RoomOption[] = [];

/**
 * The room directory, for choosing where an onsite event is held.
 *
 * `include_edumate` adds the rooms that have no Outlook mailbox: a person
 * picking where to hold something wants every space, not only the ones that
 * can be booked through Outlook. The directory changes when pymble-flows syncs
 * it, so it is held for an hour rather than refetched per open.
 *
 * `enabled` holds the request off until the list is wanted, so opening the
 * wizard does not fetch rooms for an event that turns out to be offsite.
 */
export function useRooms(enabled = true) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const query = useQuery<RoomOption[]>({
		queryKey: ["room", "directory"],
		queryFn: async () => {
			const res = (await callPymbleApi(
				instance,
				"GET",
				"room/?include_edumate=true",
			)) as RoomListResponse | null;
			return Array.isArray(res?.items) ? res.items : EMPTY;
		},
		staleTime: 60 * 60 * 1000,
		enabled: isAuthenticated && enabled,
	});

	const rooms = useMemo(
		() =>
			[...(query.data ?? EMPTY)]
				.filter((r) => r?.key && r?.name)
				.sort((a, b) => a.name.localeCompare(b.name, "en-AU")),
		[query.data],
	);

	return { rooms, loaded: !query.isPending, failed: query.isError };
}
